import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { BackButton } from "@/components/BackButton";

export default function QuotePage() {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotations", form);
      setSent(true);
      toast.success("Quotation request sent");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold";

  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="quote-page">
      <Seo title="Request a Bulk Quotation" description="Request a bulk quotation for your school or business from BTMEDU — stationery packs, textbooks and corporate gifts." />
      <div className="mb-4">
        <BackButton to="/" label="Back to home" testId="quote-back-button" />
      </div>
      <Reveal>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-brand mb-2">Bulk & corporate</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Request a quotation</h1>
        <p className="text-sm text-slate-500 mt-3 max-w-lg">
          Buying for a school, class or business? Send us your stationery list and we'll come back with
          pricing for the full pack.
        </p>
        {sent ? (
          <div className="mt-8 bg-white rounded-3xl border border-emerald-200 p-10 text-center" data-testid="quote-success">
            <p className="font-display font-bold text-navy text-xl">Request received</p>
            <p className="text-sm text-slate-500 mt-2">
              We'll email your quotation to <span className="font-semibold text-navy">{form.email}</span>.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-slate-200/80 p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-name" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email" />
            </div>
            <input placeholder="School / company (optional)" value={form.organisation} onChange={set("organisation")} className={inputCls} data-testid="quote-organisation" />
            <textarea
              required
              rows={6}
              placeholder="Paste your stationery list or describe what you need, quantities, grades…"
              value={form.message}
              onChange={set("message")}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-gold"
              data-testid="quote-message"
            />
            <button
              type="submit"
              disabled={busy}
              className="h-12 px-8 rounded-full bg-brand hover:bg-brand-accent disabled:opacity-50 text-white font-bold text-sm flex items-center gap-2 transition-colors"
              data-testid="quote-submit"
            >
              <Send size={15} /> {busy ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
      </Reveal>
    </div>
  );
}
